import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { optimizeImageUrlHeight } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { trackEventOnClick } from "@faire/web--source/common/trackEvent";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import isChromatic from "chromatic/isChromatic";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";

interface INewsReport {
  mediaName: string;
  mediaLogo: string;
  content: React.ReactNode;
  cta: string;
}

const NEWS_REPORTS: INewsReport[] = [
  {
    mediaName: "fast-company",
    mediaLogo:
      "https://cdn.faire.com/fastly/8b49052255542b0b562d1087dcee07c077baa9d35aeb830a466ba8dded720f08.png",
    content: (
      <StrictLocalMsg defaultMessage="“The 10 most innovative retail companies of 2020”" />
    ),
    cta: "https://www.fastcompany.com/90457893/retail-most-innovative-companies-2020",
  },
  {
    mediaName: "wwd",
    mediaLogo:
      "https://cdn.faire.com/fastly/6eb7dbbad18a3f27d827dbb2bbfac8d3cbd15176aae3584df89768a408202638.png",
    content: (
      <StrictLocalMsg defaultMessage="“Faire’s first online trade show could show how an online version could work.”" />
    ),
    cta: "https://wwd.com/business-news/retail/faire-summer-market-live-trade-shows-1203706701/",
  },
  {
    mediaName: "forbes",
    mediaLogo:
      "https://cdn.faire.com/fastly/26f3e4ccf3e5f2e5b979150dea590bf4476edd6ed34ddfba732e67eb00b4aa7f.png",
    content: (
      <StrictLocalMsg defaultMessage="“A high-flying unicorn bet its business on local shops.”" />
    ),
    cta: "https://www.forbes.com/sites/laurendebter/2020/03/26/faire-small-businesses-coronavirus/#4780379f17c2",
  },
];

const NewsModuleBase = React.forwardRef<
  HTMLDivElement,
  {
    className?: string;
    eventPrefix: string;
  }
>((props, ref) => {
  const { strictLocalize } = useStrictLocalization();
  const { className, eventPrefix } = props;

  useEffect(() => {
    // As the news reports only render when user scroll to this section
    // we want to preload the images to avoid flickering during the fadeIn animation
    const imagesToLoad = NEWS_REPORTS.map((newsReport) => newsReport.mediaLogo);
    imagesToLoad.forEach((url) => {
      const window = getWindowOrThrow();
      const img = new window.Image();
      img.src = optimizeImageUrlHeight({ url }, false, 27) ?? "";
    });
  }, []);

  const isSSR = useIsSSR();
  const { isMobile } = useViewport();

  return (
    <Wrapper className={className} ref={ref}>
      <WhenVisibilityIsImminent
        threshold={0}
        forceRender={isMobile || isChromatic()}
      >
        <Spacer size={isMobile ? 80 : 0} />
        <NewsReportsWrapper>
          {NEWS_REPORTS.map((newsReport, index) => {
            return (
              <NewsReportColumn
                key={newsReport.mediaLogo}
                $index={index}
                $isChromatic={isChromatic()}
              >
                <Row align="center" justify="center">
                  <MediaLogo
                    alt={strictLocalize({
                      defaultMessage: "Media Logo",
                      description: {
                        text: "description of an image for accessibility",
                      },
                    })}
                    src={optimizeImageUrlHeight(
                      { url: newsReport.mediaLogo },
                      isSSR,
                      27
                    )}
                    height={27}
                  />
                </Row>
                <NewsReportContent>{newsReport.content}</NewsReportContent>
                <Button
                  variant="tertiary"
                  target="_blank"
                  linkTo={newsReport.cta}
                  onClick={trackEventOnClick(
                    `${eventPrefix}${newsReport.mediaName}`
                  )}
                >
                  <StrictLocalMsg defaultMessage="Read Article" />
                </Button>
              </NewsReportColumn>
            );
          })}
        </NewsReportsWrapper>
      </WhenVisibilityIsImminent>
    </Wrapper>
  );
});

export const NewsModule = observer(NewsModuleBase);

const Wrapper = styled(Column).attrs({ align: "center", justify: "center" })`
  max-width: 1008px;
  height: 404px;
  padding: 0 72px;
  width: 100%;
  ${responsiveMediaQueries.mobile} {
    height: 794px;
    padding: 0;
  }
`;

const NewsReportsWrapper = styled(Flex).attrs({
  direction: {
    mobileAndAbove: "column",
    tabletAndAbove: "row",
  },
  justify: {
    mobileAndAbove: "space-between",
    desktopAndAbove: "space-around",
  },
})`
  width: 100%;
`;

const NewsReportColumn = styled(Column).attrs({
  align: "center",
})<{ $index: number; $isChromatic: boolean }>`
  ${(props) =>
    !props.$isChromatic
      ? css`
          opacity: 0;
          animation: fadeIn 0.5s ease 1 forwards;
          animation-delay: ${0.5 * props.$index}s;

          @keyframes fadeIn {
            to {
              opacity: 1;
            }
          }
        `
      : ""}
  ${responsiveMediaQueries.mobile} {
    animation: none;
    opacity: 1;
    margin-bottom: 80px;
  }
`;

const MediaLogo = styled.img`
  margin-bottom: 24px;
`;

const NewsReportContent = styled(Typography)`
  &&& {
    margin-bottom: 8px;
    text-align: center;
    max-width: 272px;
    min-width: 272px;

    ${responsiveMediaQueries.tabletAndBelow} {
      max-width: 200px;
      min-width: 200px;
    }
  }
`;
